import { useState, useEffect, useCallback } from 'react';
import { useDemoMode } from '@/contexts/DemoModeContext';

interface StabilityState {
  isOnline: boolean;
  isStable: boolean;
  retryCount: number;
}

export const useStabilityEnhancements = () => {
  const { isDemoMode } = useDemoMode();
  
  const [state, setState] = useState<StabilityState>({
    isOnline: isDemoMode ? true : navigator.onLine,
    isStable: true,
    retryCount: 0
  });

  useEffect(() => {
    // Skip connection monitoring in demo mode
    if (isDemoMode) {
      setState(prev => ({ ...prev, isOnline: true, isStable: true }));
      return;
    }

    const handleOnline = () => {
      setState(prev => ({ ...prev, isOnline: true, isStable: true }));
    };

    const handleOffline = () => {
      setState(prev => ({ ...prev, isOnline: false, isStable: false }));
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [isDemoMode]);

  const withRetry = useCallback(async <T>(
    operation: () => Promise<T>,
    maxRetries: number = 3,
    delay: number = 1000
  ): Promise<T> => {
    let lastError: Error;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const result = await operation();
        setState(prev => ({ ...prev, isStable: true, retryCount: 0 }));
        return result;
      } catch (error) {
        lastError = error as Error;
        setState(prev => ({ ...prev, retryCount: attempt + 1 }));
        
        if (attempt < maxRetries) {
          setState(prev => ({ ...prev, isStable: false }));
          await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, attempt)));
        }
      }
    }
    
    setState(prev => ({ ...prev, isStable: false }));
    throw lastError!;
  }, []);

  return {
    ...state,
    withRetry
  };
};